import { Loader2, MoreHorizontal } from "lucide-react";
import { memo, useEffect, useMemo, useRef, useState } from "react";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Slider } from "@/components/ui/slider";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import { THREAD_CARD } from "../choices/ApprovalCard";
import type { WireChartTable } from "../protocol/protocol";
import type { ChartPart } from "../store/selectors";
import { AssistantCard, AssistantCardHeader } from "../theme/AssistantCard";
import { chartConfig, themedSpec, useChartTokens } from "./theme";
import { fileName, formatCell, saveCsv, saveFile } from "./table";
import type { ChartView, Drawn } from "./vegaRuntime";

type Status = "loading" | "ready" | "failed";

/** A what-if input the spec declares (a Vega-Lite param bound to an input). */
export interface ChartControl {
  name: string;
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
}

const CONTROL_NAME = /^[a-z][a-z0-9_]{0,31}$/;

/** The spec's range controls; the card draws them with our slider and sets them through the view.
 * One the slider can't hold (a bad name, a bound that is not a number, an empty range) is left out,
 * and the chart keeps its stored value. */
export function chartControls(spec: Record<string, unknown>): ChartControl[] {
  const params = Array.isArray(spec.params) ? spec.params : [];
  return params.flatMap((param) => {
    const bind = param?.bind;
    if (bind?.input !== "range" || typeof param.value !== "number") return [];
    const control = {
      name: String(param.name),
      label: String(bind.name ?? param.name),
      value: param.value,
      min: Number(bind.min ?? 0),
      max: Number(bind.max ?? param.value * 2),
      step: Number(bind.step ?? 1),
    };
    const { name, value, min, max, step } = control;
    const usable =
      CONTROL_NAME.test(name) &&
      [value, min, max, step].every(Number.isFinite) &&
      min < max &&
      step > 0;
    return usable ? [control] : [];
  });
}

/**
 * A chart a tool showed, in the thread: its title, the chart (Vega-Lite, drawn by the lazily loaded
 * Vega runtime in the app's tones), its controls, and its data as a table on request. A chart
 * that cannot be drawn shows its table instead. The values are the ones stored with it.
 */
export const ChartCard = memo(function ChartCard({ part }: { part: ChartPart }) {
  const tokens = useChartTokens();
  const box = useRef<HTMLDivElement>(null);
  const view = useRef<ChartView | null>(null);
  const [status, setStatus] = useState<Status>("loading");
  const [tableOpen, setTableOpen] = useState(false);
  const controls = useMemo(() => chartControls(part.vegalite), [part.vegalite]);
  const [values, setValues] = useState<Record<string, number>>(() =>
    Object.fromEntries(controls.map((control) => [control.name, control.value]))
  );
  const current = useRef(values);
  current.current = values;

  useEffect(() => {
    const element = box.current;
    if (!element) return;
    let cancelled = false;
    let drawn: Drawn | null = null;
    import("./vegaRuntime")
      .then(({ draw }) =>
        // A redraw (a theme change) keeps where the user left the controls.
        draw(element, themedSpec(part.vegalite, tokens), chartConfig(tokens), current.current)
      )
      .then((result) => {
        drawn = result;
        // Left (or redrawn) while it drew: this view is not the card's.
        if (cancelled) return result.finalize();
        view.current = result.view;
        setStatus("ready");
      })
      .catch(() => {
        if (!cancelled) setStatus("failed");
      });
    return () => {
      cancelled = true;
      view.current = null;
      drawn?.finalize();
    };
  }, [part.vegalite, tokens]);

  const move = (name: string, value: number) => {
    setValues((now) => ({ ...now, [name]: value }));
    void view.current?.signal(name, value).runAsync();
  };

  const savePng = async () => {
    const drawn = view.current;
    if (!drawn) return;
    // On the card's own background: a transparent PNG loses its labels in a dark viewer.
    const background = drawn.background();
    drawn.background(tokens.card);
    try {
      saveFile(await drawn.toImageURL("png", 2), fileName(part.title, "png"));
    } finally {
      drawn.background(background);
    }
  };

  const failed = status === "failed";
  return (
    <AssistantCard className={THREAD_CARD} data-chart="">
      <AssistantCardHeader title={part.title}>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              aria-label="Chart options"
              className="h-7 w-7 text-muted-foreground max-sm:h-10 max-sm:w-10"
            >
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {!failed && (
              <DropdownMenuItem onSelect={() => setTableOpen((open) => !open)}>
                {tableOpen ? "Hide table" : "Show table"}
              </DropdownMenuItem>
            )}
            <DropdownMenuItem disabled={status !== "ready"} onSelect={() => void savePng()}>
              Download PNG
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => saveCsv(part.table, part.title)}>
              Download CSV
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </AssistantCardHeader>
      {!failed && (
        <div className="relative px-3.5 pb-3 max-sm:px-3">
          <div
            ref={box}
            role="img"
            aria-label={part.title}
            data-chart-box=""
            className="h-64 w-full max-sm:h-56"
          />
          {status === "loading" && (
            <div className="absolute inset-0 flex items-center justify-center">
              <Loader2
                aria-label="Loading chart"
                className="h-4 w-4 text-muted-foreground motion-safe:animate-spin"
              />
            </div>
          )}
        </div>
      )}
      {!failed &&
        controls.map((control) => (
          <ControlRow
            key={control.name}
            control={control}
            value={values[control.name] ?? control.value}
            money={part.table.columns.some((column) => column.unit === "usd")}
            disabled={status !== "ready"}
            onChange={(value) => move(control.name, value)}
          />
        ))}
      {(failed || tableOpen) && <ChartTable title={part.title} table={part.table} />}
    </AssistantCard>
  );
});

const MONEY = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" });

function ControlRow({
  control,
  value,
  money,
  disabled,
  onChange,
}: {
  control: ChartControl;
  value: number;
  money: boolean;
  disabled: boolean;
  onChange: (value: number) => void;
}) {
  const shown = money ? MONEY.format(value) : String(value);
  return (
    <div className="flex items-center gap-3 px-3.5 pb-3 text-xs max-sm:px-3">
      <span aria-hidden className="shrink-0 text-muted-foreground">
        {control.label}
      </span>
      <Slider
        thumbLabel={control.label}
        thumbValueText={shown}
        min={control.min}
        max={control.max}
        step={control.step}
        value={[value]}
        disabled={disabled}
        onValueChange={([next]) => onChange(next)}
        // 44px tall on a phone, the touch target (the thumb is absolute, so it adds no height).
        className="max-sm:h-11"
      />
      <span className="w-20 shrink-0 text-right font-medium tabular-nums">{shown}</span>
    </div>
  );
}

/** Memoized: a slider's move re-renders the card, never the rows (the table keeps stored values). */
const ChartTable = memo(function ChartTable({
  title,
  table,
}: {
  title: string;
  table: WireChartTable;
}) {
  return (
    <div className="max-h-72 overflow-auto border-t border-border/60" data-chart-table="">
      <Table aria-label={title} className="text-xs">
        <TableHeader>
          <TableRow>
            {table.columns.map((column) => (
              <TableHead
                key={column.key}
                className={column.type === "number" ? "h-8 text-right" : "h-8"}
              >
                {column.label}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {table.rows.map((row, index) => (
            <TableRow key={index}>
              {table.columns.map((column) => (
                <TableCell
                  key={column.key}
                  className={column.type === "number" ? "py-1.5 text-right tabular-nums" : "py-1.5"}
                >
                  {formatCell(column, row[column.key])}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
});
